import { CancelError } from "@/error/CancelError";

/**
 * Creates a value at most once: concurrent callers join the pending creation, later ones get the created value
 * until it is released. A failed creation is not kept, so the next call creates again.
 * Releasing a pending creation hands it to the releaser and makes the next call create again.
 * The joined callers get a CancelError instead, so that nobody works with a disposed value.
 */
export class SharedResource<T extends object> {
  private promise: Promise<T> | undefined;
  private created: T | undefined;
  private generation = 0;

  value(): T | undefined {
    return this.created;
  }

  async acquire(task: () => Promise<T>): Promise<T> {
    if (this.created !== undefined) {
      return this.created;
    }
    const generation = this.generation;
    const value = await this.create(task);
    if (generation !== this.generation) {
      throw new CancelError("Resource was released while it was created");
    }
    return value;
  }

  async release(): Promise<T | undefined> {
    const released = this.created ?? this.promise;
    this.created = undefined;
    this.promise = undefined;
    this.generation++;
    try {
      return await released;
    } catch (_) {
      // a failed creation holds nothing to release, the error is reported to the acquire() caller
    }
  }

  private create(task: () => Promise<T>): Promise<T> {
    if (this.promise) {
      return this.promise;
    }
    const promise = task().then(
      (value) => {
        // taken over in the same tick as the creation settles, so that a release always finds one of the two
        if (this.promise === promise) {
          this.created = value;
        }
        return value;
      },
      (error) => {
        if (this.promise === promise) {
          this.promise = undefined;
        }
        throw error;
      },
    );
    return (this.promise = promise);
  }
}
